import React from "react";
import type { ThoughtEndorsementRecord } from "@/main/common/EndorsementTypes";
import type { ThoughtContentRecord } from "@/main/common/ThoughtEditTypes";
import { MetaHandler } from "@/main/common/MetaHandler";
import type { DiscussionVoteState } from "@/main/common/DiscussionVoteState";

/**
 * Remembers **the viewer's own actions** for the lifetime of the tab, so they survive the control that
 * performed them being unmounted or re-seeded from a stale DTO.
 *
 * Why this exists: a control seeds from an `initially*` prop, and that prop can be older than the
 * action. Three routes produce it, and only the first needs a collapsible:
 *
 * 1. **An unmount.** Radix `AccordionContent` / `Collapsible` / tab panels unmount their subtree; the
 *    remount re-seeds from a bootstrap payload fetched before the click and never refreshed. Reported
 *    2026-07-24: follow a series in the home rail, collapse, expand, the button reads "Follow" again.
 * 2. **A prop-resync effect**, which needs no unmount at all — any re-render re-supplying the stale
 *    bootstrap prop clobbers the state.
 * 3. **A soft-navigation Back.** `PaginatedListWrapperV2` banks a list's `items` BY REFERENCE into
 *    `NavigationListStateCache`, an action in `useState` never writes into that array, and the restored
 *    list deliberately outranks fresh server data — so Back re-seeds every card pre-action. Reported
 *    2026-08-09 on discussion upvotes; a reload "fixes" it because this map dies with the tab.
 *
 * `EventRegistry` cannot solve any of them: it is a live broadcast with nothing retained, so a control
 * that was unmounted when the event fired never hears it and has nothing to read at mount.
 *
 * Design: `designs/01-now/2026-07-24-viewer-relationship-state-survives-unmount-design.md`
 * (§"From one store to a registry" for the 2026-08-09 generalization).
 *
 * Deliberate properties, each load-bearing:
 *
 * - **Viewer-scoped keys**, so signing in as another account cannot read back the previous viewer's
 *   state, and nothing is stored at all while logged out.
 * - **Bounded** ({@link MAX_ENTRIES}, oldest-write-first eviction), so a long browsing session cannot
 *   grow it without limit.
 * - **Session-only, never persisted.** A reload refetches real server state, which is authoritative; a
 *   persisted cache could only be more wrong than the server.
 * - **`globalThis`-pinned**, so `setupTests.ts` can clear it per test THROUGH THE GLOBAL rather than by
 *   importing this module. Pulling an `@/main/common/*` module into `setupFilesAfterEnv` registers it
 *   before specs install their `jest.mock`s, which took clientLogger, AjaxAuthSessionHandler and
 *   MessagesDrawer red.
 * - **Not a `useSyncExternalStore` store.** No snapshot, no subscribers, no `notifyListeners` — so rule
 *   31 does not bind it and an action here cannot re-render the persistent chrome. If a later revision
 *   exposes it through a store, rule 31's no-op-write bail-out becomes mandatory.
 */

/** A group pin is a boolean AND the id needed to undo it, which is why the payload type is open. */
export type DiscussionGroupPinRecord = {
    pinned: boolean;
    pinId: number | null;
};

/**
 * THE REGISTRY. Adding a viewer action to it is one line here plus one line at the call site; that is
 * the entire extension point, and it is what keeps this from becoming a fourth parallel store.
 *
 * A kind belongs here when it is **an action the viewer took**, whose only other record is a DTO that
 * can go stale. It does NOT belong here when the value is content or permission state
 * (`isDeleted`, `canDelete`, `canStartConversation`), nor when a parent component already owns it on
 * purpose — `ThoughtButtons`' `accepted` and `mindChanged` are drawn by the card as well as the
 * button, and a record here would create exactly the second copy that arrangement exists to prevent.
 */
export type ViewerActionRecordMap = {
    "series-follow": boolean;
    "user-follow": boolean;
    "group-membership": boolean;
    "group-join-request": boolean;
    "discussion-vote": DiscussionVoteState;
    "discussion-bookmark": boolean;
    // Follow a post (question loop, Part A). Written from the server's answer only: the toggle's own
    // PUT/DELETE, its first GET, and the notification row's "Stop following".
    "discussion-follow": boolean;
    "discussion-archived": boolean;
    "discussion-group-exclusion": boolean;
    "discussion-group-pin": DiscussionGroupPinRecord;
    "thought-vote": DiscussionVoteState;
    "thought-bookmark": boolean;
    "thought-pinned": boolean;
    // The viewer's own endorsement on a thought, plus the chip's count and kinds, as ONE flat record
    // (`kindsCsv`, never an array — see `isSameRecord`). Written only from the server's answer.
    "thought-endorsement": ThoughtEndorsementRecord | null;
    // The viewer's own EDIT of their own reply (2026-09-10, editable replies design §3.6): the new HTML,
    // the "Edited" timestamp, the spoiler flag and the version count, as ONE flat record written only from
    // the server's answer. This is content, which the rule below keeps out — but it is content the VIEWER
    // just changed, whose only other copy is a list DTO that `NavigationListStateCache` restores stale on
    // Back. That is exactly the case this registry exists for; the card resolves `record ?? prop`.
    "thought-content": ThoughtContentRecord | null;
    // The THOUGHTS the viewer just sent from a short post's feed-card reply bar (SV-141 D3; retyped to
    // thoughts by SV-147 A5), keyed by the POST's publicId, oldest first. Rendered under the card from the
    // submitted text, so a Back-restored feed (whose DTOs predate the reply) still shows it (rule 33).
    // One entry per `thoughtPublicId`, so a repeated answer for the same thought never adds a second. An
    // ARRAY payload, so it is read through `getViewerActionRecord` and written through
    // `rememberViewerActionRecord` only, never through `useViewerActionRecord`, whose shallow
    // `isSameRecord` guard is for flat payloads.
    "short-post-inline-replies": readonly ShortPostInlineReplyRecord[];
};

/** One thought the viewer sent from a short post's feed card, as the card renders it (SV-147 A5). */
export type ShortPostInlineReplyRecord = {
    thoughtPublicId: string;
    /** The thought on its post's page: `/discussion/{postPublicId}#argument-{thoughtPublicId}`. */
    permalink: string;
    text: string;
    authorUsername: string;
};

export type ViewerActionKind = keyof ViewerActionRecordMap;

/** Retained for the four follow/join call sites and the review lens that names it. */
export type ViewerRelationshipKind = Extract<
    ViewerActionKind,
    "series-follow" | "user-follow" | "group-membership" | "group-join-request"
>;

const GLOBAL_STORE_KEY = "__seavienViewerActionRecords" as const;
const MAX_ENTRIES = 500;

type StoreHost = typeof globalThis & {
    [GLOBAL_STORE_KEY]?: Map<string, unknown>;
};

function getStore(): Map<string, unknown> {
    const host = globalThis as StoreHost;
    if (!host[GLOBAL_STORE_KEY]) {
        host[GLOBAL_STORE_KEY] = new Map<string, unknown>();
    }
    return host[GLOBAL_STORE_KEY];
}

function buildKey(kind: ViewerActionKind, resourceId: string | null | undefined): string | null {
    const viewerUsername = MetaHandler.getLoggedInUsername();
    const normalizedResourceId = resourceId?.trim();
    if (!viewerUsername || !normalizedResourceId) {
        return null;
    }
    return `${kind}:${viewerUsername}:${normalizedResourceId}`;
}

/**
 * The viewer's last known state for this resource, or `undefined` if they have not acted on it here.
 *
 * `undefined` is the load-bearing return: it is what makes the record win **only** when the viewer
 * actually acted, leaving every untouched card reading its prop exactly as before.
 */
export function getViewerActionRecord<K extends ViewerActionKind>(
    kind: K,
    resourceId: string | null | undefined,
): ViewerActionRecordMap[K] | undefined {
    const key = buildKey(kind, resourceId);
    if (!key) {
        return undefined;
    }
    // The store is heterogeneous by design; the cast is sound because `buildKey` namespaces by kind and
    // `rememberViewerActionRecord` is the only writer, typed against the same map.
    return getStore().get(key) as ViewerActionRecordMap[K] | undefined;
}

/** Record what the viewer just did (and what the server confirmed, where the server answers with it). */
export function rememberViewerActionRecord<K extends ViewerActionKind>(
    kind: K,
    resourceId: string | null | undefined,
    value: ViewerActionRecordMap[K],
): void {
    const key = buildKey(kind, resourceId);
    if (!key) {
        return;
    }
    const store = getStore();
    // Re-insert so the Map's insertion order stays least-recently-written first.
    store.delete(key);
    store.set(key, value);
    while (store.size > MAX_ENTRIES) {
        const oldestKey = store.keys().next().value;
        if (oldestKey === undefined) {
            break;
        }
        store.delete(oldestKey);
    }
}

/**
 * What a control should MOUNT with: the record when the viewer has acted here, the DTO otherwise.
 *
 * For controls that cannot use {@link useViewerActionRecord} because they spread one record across
 * several `useState`s — both vote bars do, since the counts and flags are read individually all
 * through their render. They seed through this and write through {@link rememberViewerActionRecord}.
 */
export function resolveViewerActionRecord<K extends ViewerActionKind>(
    kind: K,
    resourceId: string | null | undefined,
    fromProps: ViewerActionRecordMap[K],
): ViewerActionRecordMap[K] {
    return getViewerActionRecord(kind, resourceId) ?? fromProps;
}

/** Test-only reset. Production code never needs this — the records die with the tab. */
export function clearViewerActionRecords(): void {
    getStore().clear();
}

/**
 * Content equality for a record. Payloads are primitives or FLAT objects by construction, so a shallow
 * compare is exact rather than an approximation.
 *
 * **This is what makes an object payload safe in the hook below, and it is not optional.** While every
 * kind held a boolean, `initialValue` was a stable primitive and identity comparison was enough. An
 * object payload is rebuilt by the caller on every render, so an identity-compared resync effect would
 * `setValue` a new object each pass, re-render, and run again — React #185, arrived at from a module
 * that is not a `useSyncExternalStore` store and therefore not covered by rule 31's guard tests. The
 * discipline is the same one rule 31 states: a write that changes nothing must change no identity.
 */
function isSameRecord(a: unknown, b: unknown): boolean {
    if (Object.is(a, b)) {
        return true;
    }
    if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) {
        return false;
    }
    const aKeys = Object.keys(a as Record<string, unknown>);
    const bKeys = Object.keys(b as Record<string, unknown>);
    if (aKeys.length !== bKeys.length) {
        return false;
    }
    return aKeys.every((key) => Object.is(
        (a as Record<string, unknown>)[key],
        (b as Record<string, unknown>)[key],
    ));
}

/**
 * `useState` for a viewer action, backed by the registry.
 *
 * - **initializes** from the record, falling back to the server-supplied `initialValue` — this is what
 *   survives the unmount/remount cycle and the Back-restored stale DTO;
 * - **writes through** on every set, so the optimistic update, the failure revert and the
 *   `EventRegistry` cross-component sync all keep the record current with no extra call sites;
 * - **resyncs on prop change** to `record ?? initialValue` rather than blindly to `initialValue`, so a
 *   stale bootstrap prop cannot clobber an action the viewer already took, while a genuinely new
 *   resource id still re-seeds.
 *
 * The direction matters and only goes one way: a control seeds FROM the record and never writes its own
 * props INTO it. `ViewerDiscussionAgreementStore` did the latter in a mount effect, so a Back-restored
 * card overwrote the correct remembered vote with "no vote".
 */
export function useViewerActionRecord<K extends ViewerActionKind>(
    kind: K,
    resourceId: string | null | undefined,
    initialValue: ViewerActionRecordMap[K],
): [ViewerActionRecordMap[K], (next: ViewerActionRecordMap[K]) => void] {
    const [value, setValue] = React.useState<ViewerActionRecordMap[K]>(
        () => getViewerActionRecord(kind, resourceId) ?? initialValue,
    );

    // Read inside the effect and the setter so neither has to depend on `value`, which would make the
    // effect re-run on its own output.
    const valueRef = React.useRef(value);
    valueRef.current = value;

    // `initialValue` stays in the deps so a genuinely new server value still re-seeds an untouched
    // control; the content guard is what keeps a re-created object from turning that into a loop.
    React.useEffect(() => {
        const next = getViewerActionRecord(kind, resourceId) ?? initialValue;
        if (!isSameRecord(next, valueRef.current)) {
            setValue(next);
        }
    }, [kind, resourceId, initialValue]);

    const setAndRemember = React.useCallback((next: ViewerActionRecordMap[K]) => {
        // The record is written unconditionally — "same content" does not mean "already recorded", and
        // the record existing at all is what makes it outrank the prop later.
        rememberViewerActionRecord(kind, resourceId, next);
        if (!isSameRecord(next, valueRef.current)) {
            setValue(next);
        }
    }, [kind, resourceId]);

    return [value, setAndRemember];
}

/**
 * The follow/join alias the 2026-07-24 call sites and the review lens use. Kept so those four sites do
 * not churn; it is the same function, narrowed to the boolean kinds.
 */
export function useViewerRelationshipState(
    kind: ViewerRelationshipKind,
    resourceId: string | null | undefined,
    initialValue: boolean,
): [boolean, (next: boolean) => void] {
    return useViewerActionRecord(kind, resourceId, initialValue);
}
